import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal } from "@/components/bits";
import ProcessScroll from "@/components/ProcessScroll";
import NinetyDays from "@/components/NinetyDays";
import CTASection from "@/components/CTASection";
import { SEO } from "@/lib/site";

const CHAIN = ["Diagnose", "Baseline", "Prioritize", "Deploy", "Operate", "Measure", "Scale"];

const HowItWorks = () => {
    useSEO({ title: SEO["/how-it-works"].title, description: SEO["/how-it-works"].description, path: "/how-it-works" });

    return (
        <>
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="how-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_30%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow>How It Works</Eyebrow>
                    <Masked
                        lines={["Turn the Business You Already Have", "Into a More Powerful", "Profit Engine."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.05] tracking-tightest sm:text-6xl lg:text-[72px]"
                    />
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slategray sm:text-xl">
                        Vantera works around your existing people, systems, customers, vendors, and data. We diagnose the economics first, then focus resources on the opportunities most likely to improve revenue, margin, and operating efficiency.
                    </p>
                    <Reveal delay={0.35} className="mt-10 flex flex-wrap gap-2.5" data-testid="how-chain">
                        {CHAIN.map((c) => (
                            <span key={c} className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                                {c} <span className="text-vantera">{c !== "Scale" ? "→" : ""}</span>
                            </span>
                        ))}
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 sm:pb-32" data-testid="how-process">
                <div className="container-x">
                    <ProcessScroll />
                </div>
            </section>

            <section className="relative overflow-hidden bg-ink py-20 text-ivory sm:py-24" data-testid="how-formula">
                <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="container-x relative text-center">
                    <Eyebrow dark className="justify-center">The prioritization formula</Eyebrow>
                    <p className="mx-auto mt-8 max-w-4xl font-display text-2xl font-extrabold leading-snug tracking-tight sm:text-4xl lg:text-5xl">
                        Economic Impact <span className="text-vantera">×</span> Speed <span className="text-vantera">×</span> Confidence <span className="text-vantera">×</span> Effort
                    </p>
                    <p className="mx-auto mt-6 max-w-xl text-lg text-cloud">
                        Every initiative is ranked by its economics — so resources never scatter across disconnected tactics.
                    </p>
                </div>
            </section>

            <section className="py-24 sm:py-32" data-testid="how-ninety">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Eyebrow>The first 90 days</Eyebrow>
                        <Masked inview lines={["Momentum, in Three Phases."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <div className="mt-16">
                        <NinetyDays />
                    </div>
                </div>
            </section>

            <CTASection />
        </>
    );
};

export default HowItWorks;